import type { CSSProperties } from "react";

interface Props {
  size?: number;
  /** Color of the strokes. Defaults to currentColor. */
  color?: string;
  /** Color of the lure dot. Defaults to var(--accent). */
  lureColor?: string;
  /** Whether the lure pulses slowly (bioluminescent breath). */
  pulse?: boolean;
  style?: CSSProperties;
  title?: string;
}

/**
 * Anglerfish logomark. Stylized predator: a V (jaw silhouette), a single
 * stalk, and a luminescent dot at the top (the lure). Drawn at 24×24 for
 * crisp pixel snapping at common header sizes.
 *
 * The single asymmetric element makes this read distinct from the dozens of
 * generic geometric ops marks — it's specifically an anglerfish, even at
 * 14px.
 */
export function AnglerfishMark({
  size = 22,
  color = "currentColor",
  lureColor = "var(--accent)",
  pulse = false,
  style,
  title = "Anglerfish",
}: Props) {
  return (
    <svg
      role="img"
      aria-label={title}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={style}
    >
      <title>{title}</title>
      {/* Jaw — angular V */}
      <path
        d="M 3.5 11.5 L 12 21 L 20.5 11.5"
        stroke={color}
        strokeWidth={1.4}
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
      {/* Inner jaw notch — subtle interior line giving the mark a "teeth" hint */}
      <path
        d="M 7.5 11.5 L 12 16.5 L 16.5 11.5"
        stroke={color}
        strokeWidth={0.6}
        strokeLinecap="square"
        opacity={0.55}
      />
      {/* Lure stalk */}
      <line
        x1="12"
        y1="9"
        x2="12"
        y2="4.5"
        stroke={color}
        strokeWidth={1.1}
        strokeLinecap="square"
      />
      {/* Lure dot — bioluminescent */}
      <circle
        cx="12"
        cy="3.2"
        r="1.65"
        fill={lureColor}
        className={pulse ? "lure" : undefined}
      />
      {/* Outer glow ring — only when pulsing */}
      {pulse ? (
        <circle
          cx="12"
          cy="3.2"
          r="3"
          fill="none"
          stroke={lureColor}
          strokeWidth={0.5}
          opacity={0.35}
        />
      ) : null}
    </svg>
  );
}
